import React, { Suspense, lazy, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth.js";
import { OrgProvider } from "./lib/org.js";

const LoginPage = lazy(() => import("./pages/Login.js"));
const DashboardPage = lazy(() => import("./pages/Dashboard.js"));
const OnboardingWizardPage = lazy(() => import("./pages/OnboardingWizard.js"));
const AdminQueuePage = lazy(() => import("./pages/AdminQueue.js"));
const OrgDetailPage = lazy(() => import("./pages/OrgDetail.js"));
const MembersPage = lazy(() => import("./pages/Members.js"));
const ProductsPage = lazy(() => import("./pages/Products.js"));
const InviteAcceptPage = lazy(() => import("./pages/InviteAccept.js"));
const AdminOrgsPage = lazy(() => import("./pages/AdminOrgs.js"));
const AdminOrgDetailPage = lazy(() => import("./pages/AdminOrgDetail.js"));
const AdminUsersPage = lazy(() => import("./pages/AdminUsers.js"));

const PENDING_INVITE_KEY = "identity_pending_invite";

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function RequireSuperadmin({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.platform_role !== "superadmin") return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

/**
 * After a successful login (user transitions from null to non-null),
 * redirect to the pending invite URL if one was saved.
 */
function PendingInviteHandler() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) {
      const pending = localStorage.getItem(PENDING_INVITE_KEY);
      if (pending) {
        localStorage.removeItem(PENDING_INVITE_KEY);
        navigate(pending, { replace: true });
      }
    }
  }, [user, loading]);

  return null;
}

function LoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <p className="text-sm text-muted-foreground">Cargando...</p>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <OrgProvider>
        <Toaster position="top-right" richColors />
        <PendingInviteHandler />
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            {/* Public */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/join/:token" element={<InviteAcceptPage />} />

            {/* Authenticated */}
            <Route
              path="/dashboard"
              element={
                <RequireAuth>
                  <DashboardPage />
                </RequireAuth>
              }
            />
            <Route
              path="/onboarding"
              element={
                <RequireAuth>
                  <OnboardingWizardPage />
                </RequireAuth>
              }
            />
            <Route
              path="/empresa"
              element={
                <RequireAuth>
                  <OrgDetailPage />
                </RequireAuth>
              }
            />
            <Route
              path="/miembros"
              element={
                <RequireAuth>
                  <MembersPage />
                </RequireAuth>
              }
            />
            <Route
              path="/productos"
              element={
                <RequireAuth>
                  <ProductsPage />
                </RequireAuth>
              }
            />

            {/* Superadmin */}
            <Route
              path="/admin/applications"
              element={
                <RequireSuperadmin>
                  <AdminQueuePage />
                </RequireSuperadmin>
              }
            />
            <Route
              path="/admin/organizations"
              element={
                <RequireSuperadmin>
                  <AdminOrgsPage />
                </RequireSuperadmin>
              }
            />
            <Route
              path="/admin/organizations/:orgId"
              element={
                <RequireSuperadmin>
                  <AdminOrgDetailPage />
                </RequireSuperadmin>
              }
            />
            <Route
              path="/admin/users"
              element={
                <RequireSuperadmin>
                  <AdminUsersPage />
                </RequireSuperadmin>
              }
            />

            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
      </OrgProvider>
    </AuthProvider>
  );
}
